import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Card, CardContent,
  FormControl, Select, MenuItem,
  Button, TextField, Fade, Divider, CircularProgress
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import Navbar from '../../shared/Navbar';
import { COLORS } from '../../../shared/colors';
import { toast } from 'react-toastify';
import '../project-alerts-setting/ProjectAlerts.css';
import './SprintSettings.css';
import { fetchSprintSettings, updateSprintSettings } from '../../../store/scrumSlice';
import { SPRINT_SETTINGS_CONFIG, USER_ROLES, RESTRICTED_NUMBER_KEYS } from '../../../shared/appConstants';
import { REGEX } from '../../../shared/regex';

const PROJECT_ALERT_COLOR_VARS = {
  '--project-alert-panel-border': COLORS.brand.primaryDark,
  '--project-alert-card-bg': COLORS.white,
};

const DURATION_OPTIONS = ['7 days', '14 days', '21 days', '28 days'];

const SprintSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { sprintSettings, sprintSettingsLoading, actionLoading } = useSelector((state) => state.scrum);
  const { user } = useSelector((state) => state.auth);
  const isAuthorized = user?.role === USER_ROLES.ADMIN || user?.role === USER_ROLES.PM;

  const isLoading = sprintSettingsLoading || actionLoading;
  
  const [localStoryPoints, setLocalStoryPoints] = useState('');
  const [localDuration, setLocalDuration] = useState('');

  const originalPoints = useMemo(() => {
    if (!sprintSettings) return '';
    const points = sprintSettings?.json_value?.max_story_points;
    return points ? String(points) : '40';
  }, [sprintSettings]);

  const originalDuration = useMemo(() => {
    if (!sprintSettings) return '';
    const duration = sprintSettings?.json_value?.duration;
    return duration ? `${duration} days` : '21 days';
  }, [sprintSettings]);

  const isDirty = localStoryPoints !== originalPoints || localDuration !== originalDuration;

  useEffect(() => {
    if (!isAuthorized) return;
    dispatch(fetchSprintSettings())
      .unwrap()
      .then((res) => {
        const data = res?.data || res;
        if (!data?.json_value?.max_story_points || !data?.json_value?.duration) {
          dispatch(updateSprintSettings({ storyPoints: '40', sprintDuration: '21 days' }));
        }
      })
      .catch((err) => {
        if (err === 'Sprint settings not found' || (typeof err === 'string' && err.includes('404'))) {
          dispatch(updateSprintSettings({ storyPoints: '40', sprintDuration: '21 days' }));
        }
      });
  }, [dispatch, isAuthorized]);

  useEffect(() => {
    setLocalStoryPoints(originalPoints);
    setLocalDuration(originalDuration);
  }, [originalPoints, originalDuration]);

  const handleSaveChanges = async () => {
    if (!isDirty) return;
    
    const numValue = parseInt(localStoryPoints, 10);
    
    if (!localStoryPoints || isNaN(numValue) || numValue < SPRINT_SETTINGS_CONFIG.MIN_STORY_POINTS || numValue > SPRINT_SETTINGS_CONFIG.MAX_STORY_POINTS) {
      toast.error(t('sprintSettings.invalidRangeError') || 'Invalid story points range.');
      return;
    }

    try {
      await dispatch(updateSprintSettings({ storyPoints: numValue, sprintDuration: localDuration })).unwrap();
      toast.success(t('sprintSettings.saveSuccess') || 'Settings saved successfully');
    } catch (errorMsg) {
      toast.error(t('sprintSettings.saveError') || 'Failed to save settings');
    }
  };

  const handleCancel = () => {
    setLocalStoryPoints(originalPoints);
    setLocalDuration(originalDuration);
  };

  const handleImageError = useCallback((e) => {
    e.target.style.display = 'none';
  }, []);

  return (
    <div className="dashboard">
      <Navbar />
      
      <main className="main project-alerts" style={PROJECT_ALERT_COLOR_VARS}>
        <Fade in timeout={600}>
          <div>
            <div className="alerts-header">
              <button type="button" onClick={() => navigate('/settings')} className="alerts-header-link">
                {t('navigation.settings') || 'Settings'}
              </button>
              <NavigateNextIcon fontSize="small" className="alerts-header-separator sprint-settings-separator" />
              <Typography component="span" className="alerts-header-current">
                {t('sprintSettings.title') || 'Sprint settings'}
              </Typography>
              <Box
                component="img"
                src="/icons/settings.svg"
                onError={handleImageError}
                alt=""
                aria-hidden="true"
                className="alerts-header-bell"
                sx={{ width: 18, height: 18, ml: 0.5 }}
              />
            </div>

            <div className="alerts-content sprint-settings-alerts-content">
              {!isAuthorized ? (
                <Card sx={{ width: '100%', maxWidth: 680, borderRadius: 2, p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="error">Access Denied</Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: COLORS.slate[500] }}>
                    Only Scrum Masters, Product Owners, or Admins can access Sprint Settings.
                  </Typography>
                  <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate('/settings')}>
                    Go Back
                  </Button>
                </Card>
              ) : (
                <Card sx={{ 
                  width: '100%', 
                  maxWidth: 680, 
                  borderRadius: 2, 
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                  border: `1px solid ${COLORS.slate[200]}`
                }}>
                  <CardContent sx={{ p: 4, '&:last-child': { pb: 3 } }}>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.slate[900], mb: 0.5 }}>
                        {t('sprintSettings.configuration') || 'Sprint configuration'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
                        {t('sprintSettings.configurationDesc') || 'Defaults applied to every new sprint in this project.'}
                      </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Story Points */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, minHeight: 40 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.slate[800], mb: 0.5 }}>
                        {t('sprintSettings.storyPoints') || 'Story points per sprint'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
                        {t('sprintSettings.storyPointsDesc') || 'Total capacity used for sprint planning and burndown.'}
                      </Typography>
                    </Box>
                    <TextField
                      size="small"
                      value={localStoryPoints}
                      onChange={(e) => setLocalStoryPoints(e.target.value.replace(REGEX.nonNumeric, ''))}
                      onKeyDown={(e) => { if (RESTRICTED_NUMBER_KEYS.includes(e.key)) e.preventDefault(); }}
                      type="number"
                      disabled={isLoading}
                      inputProps={{ min: 1, max: 200, className: 'sprint-settings-input-center' }}
                      sx={{ 
                        width: 100,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: COLORS.white
                        }
                      }}
                    />
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Sprint Duration */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, minHeight: 40 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: COLORS.slate[800], mb: 0.5 }}>
                        {t('sprintSettings.sprintDuration') || 'Sprint duration'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
                        {t('sprintSettings.sprintDurationDesc') || 'Length of each sprint cycle.'}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ width: 140 }}>
                      <Select
                        value={localDuration}
                        onChange={(e) => setLocalDuration(e.target.value)}
                        disabled={isLoading}
                        sx={{ 
                          borderRadius: 1.5,
                          bgcolor: COLORS.white,
                          '& .MuiSelect-select': { py: 1.2 }
                        }}
                      >
                        {DURATION_OPTIONS.map(opt => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Divider sx={{ mb: 3 }} />

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCancel}
                      disabled={isLoading || !isDirty}
                      sx={{ 
                        borderColor: COLORS.ui.border, 
                        color: COLORS.text.primary,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 3,
                        borderRadius: 1.5,
                        '&:hover': {
                          borderColor: COLORS.brand.primaryDark,
                          bgcolor: COLORS.slate[50]
                        },
                        '&.Mui-disabled': {
                          bgcolor: COLORS.slate[50],
                          color: COLORS.slate[400],
                          borderColor: COLORS.ui.border
                        }
                      }}
                    >
                      {t('common.cancel') || 'Cancel'}
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSaveChanges}
                      disabled={isLoading || !isDirty}
                      sx={{ 
                        bgcolor: COLORS.brand.primaryDark, 
                        color: COLORS.white,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 3,
                        borderRadius: 1.5,
                        '&:hover': {
                          bgcolor: COLORS.brand.primaryDarkHover
                        },
                        '&.Mui-disabled': {
                          bgcolor: COLORS.slate[300],
                          color: COLORS.slate[500]
                        }
                      }}
                    >
                      {isLoading ? <CircularProgress size={20} color="inherit" /> : t('common.save') || 'Save changes'}
                    </Button>
                  </Box>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </Fade>
      </main>
    </div>
  );
};

export default SprintSettings;

